import { useEffect, useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Clock, RefreshCw, TrendingUp, TrendingDown, ArrowRight } from 'lucide-react'
import { clsx } from 'clsx'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import { api } from '../lib/api'
import ShareViewButton from '../components/ShareViewButton'

interface SnapshotPoint { date: string; value: number | string | null }

const METRIC_LABELS: Record<string, string> = {
  cves_total_7d:           'CVEs published (7d)',
  cves_critical_7d:        'Critical CVEs (7d)',
  cves_high_7d:            'High CVEs (7d)',
  kev_added_7d:            'KEV additions (7d)',
  ransomware_victims_7d:   'Ransomware victims (7d)',
  threat_actors_total:     'Threat actors catalogued',
  kev_total_cumulative:    'KEV cumulative',
  cves_total_cumulative:   'CVEs cumulative',
}

const METRIC_COLORS: Record<string, string> = {
  cves_total_7d:           '#3b82f6',
  cves_critical_7d:        '#f43f5e',
  cves_high_7d:            '#f59e0b',
  kev_added_7d:            '#ef4444',
  ransomware_victims_7d:   '#a855f7',
  threat_actors_total:     '#06b6d4',
  kev_total_cumulative:    '#ef4444',
  cves_total_cumulative:   '#3b82f6',
}

export default function TimeMachinePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlDays = parseInt(searchParams.get('days') || '30', 10)
  const [days, setDays] = useState(Number.isFinite(urlDays) ? urlDays : 30)
  const [data, setData] = useState<{ series: Record<string, SnapshotPoint[]>; metric_kinds: string[] } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const next = new URLSearchParams()
    if (days !== 30) next.set('days', String(days))
    setSearchParams(next, { replace: true })
  }, [days, setSearchParams])

  useEffect(() => {
    let mounted = true
    setLoading(true)
    api.get('/api/cyber/time-machine', { params: { days_back: days } })
      .then(r => { if (mounted) setData(r.data) })
      .catch(() => { if (mounted) setData(null) })
      .finally(() => { if (mounted) setLoading(false) })
  }, [days])

  const summary = useMemo(() => {
    if (!data?.series) return []
    return Object.entries(data.series).map(([kind, pts]) => {
      const nums = pts.map(p => typeof p.value === 'number' ? p.value : Number(p.value)).filter(n => !isNaN(n))
      const first = nums[0]
      const last  = nums[nums.length - 1]
      const delta = (first != null && last != null) ? last - first : null
      const pct   = (first != null && last != null && first !== 0) ? Math.round((delta! / first) * 100) : null
      return { kind, label: METRIC_LABELS[kind] || kind, color: METRIC_COLORS[kind] || '#888',
               first, last, delta, pct, points: pts.length }
    })
  }, [data])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Clock size={24} className="text-cyan" /> Time-machine
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Daily snapshots of headline metrics — scrub backwards to see how the cyber landscape moved
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0 rounded-md border border-border overflow-hidden text-[10px]">
            {[7, 30, 90, 180, 365].map(d => (
              <button key={d} type="button" onClick={() => setDays(d)}
                className={clsx('px-2 py-1', days === d ? 'bg-blue/20 text-blue-light font-semibold' : 'text-text-muted hover:bg-surface-hover')}>
                {d}d
              </button>
            ))}
          </div>
          <ShareViewButton />
        </div>
      </div>

      {loading && <div className="card p-6 text-center text-text-muted text-sm">Loading time-machine…</div>}

      {!loading && summary.length === 0 && (
        <div className="card p-6 text-center text-text-muted text-sm space-y-2">
          <p>No daily snapshots captured yet.</p>
          <p className="text-[11px]">
            The <code className="font-mono bg-surface-raised px-1">take_daily_snapshot</code> Celery task runs at 00:30 UTC.
            New deployments take a day to start showing data; back-fill is one-shot manual.
          </p>
        </div>
      )}

      {!loading && summary.length > 0 && (
        <>
          {/* Summary tiles — first→last delta per metric */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {summary.map(s => {
              const Icon = (s.delta ?? 0) > 0 ? TrendingUp : (s.delta ?? 0) < 0 ? TrendingDown : ArrowRight
              const tone = (s.delta ?? 0) > 0 ? 'text-rose-light' : (s.delta ?? 0) < 0 ? 'text-emerald-light' : 'text-text-muted'
              return (
                <div key={s.kind} className="card p-3">
                  <div className="text-[10px] uppercase tracking-wider text-text-muted">{s.label}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-2xl font-bold text-text-primary font-mono">{s.last ?? '—'}</span>
                    {s.delta != null && (
                      <span className={clsx('text-[11px] font-mono inline-flex items-center gap-0.5', tone)}>
                        <Icon size={10} />
                        {s.delta > 0 ? '+' : ''}{s.delta}{s.pct != null ? ` (${s.pct}%)` : ''}
                      </span>
                    )}
                  </div>
                  <div className="text-[9px] text-text-muted mt-0.5">{s.points} day{s.points === 1 ? '' : 's'} of data</div>
                </div>
              )
            })}
          </div>

          {/* Series charts */}
          {summary.map(s => {
            const series = data!.series[s.kind] || []
            if (series.length < 2) return null
            const chartData = series.map(p => ({ date: p.date.slice(5), value: typeof p.value === 'number' ? p.value : Number(p.value) }))
            return (
              <div key={s.kind} className="card p-4">
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="text-sm font-semibold text-text-primary">{s.label}</h3>
                  <span className="text-[10px] text-text-muted font-mono">range {s.first} → {s.last}</span>
                </div>
                <div style={{ width: '100%', height: 180 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData}>
                      <CartesianGrid stroke="#334155" strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="date" stroke="#64748b" tick={{ fontSize: 10 }} />
                      <YAxis stroke="#64748b" tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: '#0b1020', border: '1px solid #334155', fontSize: 11 }} />
                      <Line type="monotone" dataKey="value" stroke={s.color} strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )
          })}
        </>
      )}

      <div className="text-[10px] text-text-muted text-center">
        Snapshots captured daily at 00:30 UTC. Back-fill from cumulative tables for older dates is one-shot.
      </div>
    </div>
  )
}
